<template>
  <aside :class="classes">

    <FiltersHeader @closeFilters="close" />

    <form :class="formClasses">

      <div class="packages-overview-filters__group packages-overview-filters__group--primary">

        <Select v-model="packageTypes"
                :placeholder="filters.packageTypes.title"
                :options="filters.packageTypes.options"
                class="packages-overview-filters__input"
                />

        <DatePicker :availableDates="filters.dates.options"
                    v-model="departureDateFrom"
                    :placeholder="filters.dates.title"
                    :upperLimit="departureDateTo"
                    class="packages-overview-filters__input"
                    />

        <span class="packages-overview-filters__combiner">
          t/m
        </span>

        <DatePicker :availableDates="filters.dates.options"
                    v-model="departureDateTo"
                    :lowerLimit="departureDateFrom"
                    class="packages-overview-filters__input"
                    />

        <Select v-model="durations"
                :placeholder="filters.durations.title"
                :options="filters.durations.options"
                class="packages-overview-filters__input"
                />

        <Select v-model="airports"
                :placeholder="filters.airports.title"
                :options="filters.airports.options"
                class="packages-overview-filters__input"
                />

        <RangeSlider v-model="priceRange"
                     :min="filters.priceRange.min.value"
                     :max="filters.priceRange.max.value"
                     class="packages-overview-filters__input"
                     />

      </div>

      <div class="packages-overview-filters__group">

        <FiltersCheckboxes v-model="summerDestinations"
                           v-if="checkboxFilters.includes('summerDestinations')"
                           :open="openFilter == filters.summerDestinations.title"
                           :options="filters.summerDestinations.options"
                           :title="filters.summerDestinations.title"
                           @updateOpenFilter="updateOpenFilter"
                           />

        <FiltersCheckboxes v-model="winterDestinations"
                           v-if="checkboxFilters.includes('winterDestinations')"
                           :open="openFilter == filters.winterDestinations.title"
                           :options="filters.winterDestinations.options"
                           :title="filters.winterDestinations.title"
                           @updateOpenFilter="updateOpenFilter"
                           />

        <FiltersCheckboxes v-model="excursions"
                           v-if="checkboxFilters.includes('excursions')"
                           :open="openFilter == filters.excursions.title"
                           :options="filters.excursions.options"
                           :title="filters.excursions.title"
                           @updateOpenFilter="updateOpenFilter"
                           />

        <FiltersCheckboxes v-model="accommodationTypes"
                           v-if="checkboxFilters.includes('accommodationTypes')"
                           :open="openFilter == filters.accommodationTypes.title"
                           :options="filters.accommodationTypes.options"
                           :title="filters.accommodationTypes.title"
                           @updateOpenFilter="updateOpenFilter"
                           />

        <FiltersCheckboxes v-model="hostTypes"
                           v-if="checkboxFilters.includes('hostTypes')"
                           :open="openFilter == filters.hostTypes.title"
                           :options="filters.hostTypes.options"
                           :title="filters.hostTypes.title"
                           @updateOpenFilter="updateOpenFilter"
                           />

        <FiltersCheckboxes v-model="tourGroups"
                           v-if="checkboxFilters.includes('tourGroups')"
                           :open="openFilter == filters.tourGroups.title"
                           :options="filters.tourGroups.options"
                           :title="filters.tourGroups.title"
                           @updateOpenFilter="updateOpenFilter"
                           />

      </div>

    </form>

    <FiltersFooter v-if="amount !== null"
                   :amount="amount"
                   :loadingResults="loadingResults"
                   @closeFilters="close"
                   />

  </aside>
</template>

<script>
import _ from 'lodash';
import api from '@/api';
import {
  FILTER_TYPE_ACCOMMODATION_TYPES,
  FILTER_TYPE_AIRPORTS,
  FILTER_TYPE_DEPARTURE_DATE_FROM,
  FILTER_TYPE_DEPARTURE_DATE_TO,
  FILTER_TYPE_DESTINATIONS_SUMMER,
  FILTER_TYPE_DESTINATIONS_WINTER,
  FILTER_TYPE_DURATIONS,
  FILTER_TYPE_EXCURSIONS,
  FILTER_TYPE_HOST_TYPES,
  FILTER_TYPE_PACKAGE_TYPES,
  FILTER_TYPE_PRICE_RANGE,
  FILTER_TYPE_TOUR_GROUPS,
} from '@/constants';

import DatePicker from '@/components/UI/DatePicker.vue';
import FiltersCheckboxes from '@/components/Filters/Checkboxes.vue';
import FiltersFooter from '@/components/Filters/Footer.vue';
import FiltersHeader from '@/components/Filters/Header.vue';
import RangeSlider from '@/components/UI/RangeSlider.vue';
import Select from '@/components/UI/Select.vue';

export default {
  name: 'Filters',

  components: {
    DatePicker,
    FiltersCheckboxes,
    FiltersFooter,
    FiltersHeader,
    RangeSlider,
    Select,
  },

  props: {
    amount: {
      type: Number,
    },
    activeFilters: {
      type: Object,
      required: true,
    },
    filters: {
      type: Object,
      required: true,
    },
    loadingResults: {
      type: Boolean,
      required: true,
    },
    siteId: {
      type: Number,
      required: true,
    },
    visible: {
      type: Boolean,
      required: true,
    },
  },

  data: () => ({
    loading: false,
    openFilter: null,
    urlFilters: null,
  }),

  computed: {
    accommodationTypes: {
      get() {
        return this.getActiveFilter(FILTER_TYPE_ACCOMMODATION_TYPES);
      },
      set(value) {
        this.setActiveFilter(FILTER_TYPE_ACCOMMODATION_TYPES, value);
      },
    },

    airports: {
      get() {
        return this.getActiveFilter(FILTER_TYPE_AIRPORTS);
      },
      set(value) {
        this.setActiveFilter(FILTER_TYPE_AIRPORTS, value);
      },
    },

    checkboxFilters() {
      return [
        'summerDestinations',
        'winterDestinations',
        'excursions',
        'accommodationTypes',
        'hostTypes',
        'tourGroups',
      ].filter((checkboxFilter) => this.filters[checkboxFilter].options.length);
    },

    classes() {
      return [
        'packages-overview-filters',
        { 'packages-overview-filters--visible': this.visible },
      ];
    },

    departureDateFrom: {
      get() {
        return this.getActiveFilter(FILTER_TYPE_DEPARTURE_DATE_FROM);
      },
      set(value) {
        this.setActiveFilter(FILTER_TYPE_DEPARTURE_DATE_FROM, value);
      },
    },

    departureDateTo: {
      get() {
        return this.getActiveFilter(FILTER_TYPE_DEPARTURE_DATE_TO);
      },
      set(value) {
        this.setActiveFilter(FILTER_TYPE_DEPARTURE_DATE_TO, value);
      },
    },

    excursions: {
      get() {
        return this.getActiveFilter(FILTER_TYPE_EXCURSIONS);
      },
      set(value) {
        this.setActiveFilter(FILTER_TYPE_EXCURSIONS, value);
      },
    },

    formClasses() {
      return [
        'packages-overview-filters__form',
        { 'packages-overview-filters__form--loading': this.loading },
      ];
    },

    hasUrlFilters() {
      return (
        this.urlFilters !== null
        && Object.keys(this.urlFilters).length > 0
      );
    },

    hostTypes: {
      get() {
        return this.getActiveFilter(FILTER_TYPE_HOST_TYPES);
      },
      set(value) {
        this.setActiveFilter(FILTER_TYPE_HOST_TYPES, value);
      },
    },

    packageTypes: {
      get() {
        return this.getActiveFilter(FILTER_TYPE_PACKAGE_TYPES);
      },
      set(value) {
        this.setActiveFilter(FILTER_TYPE_PACKAGE_TYPES, value);
      },
    },

    priceRange: {
      get() {
        return FILTER_TYPE_PRICE_RANGE in this.activeFilters
          ? this.activeFilters[FILTER_TYPE_PRICE_RANGE]
          : [
            this.filters[FILTER_TYPE_PRICE_RANGE].min.value,
            this.filters[FILTER_TYPE_PRICE_RANGE].max.value,
          ];
      },
      set(value) {
        this.setActiveFilter(FILTER_TYPE_PRICE_RANGE, value);
      },
    },

    summerDestinations: {
      get() {
        return this.getActiveFilter(FILTER_TYPE_DESTINATIONS_SUMMER);
      },
      set(value) {
        this.setActiveFilter(FILTER_TYPE_DESTINATIONS_SUMMER, value);
      },
    },

    tourGroups: {
      get() {
        return this.getActiveFilter(FILTER_TYPE_TOUR_GROUPS);
      },
      set(value) {
        this.setActiveFilter(FILTER_TYPE_TOUR_GROUPS, value);
      },
    },

    winterDestinations: {
      get() {
        return this.getActiveFilter(FILTER_TYPE_DESTINATIONS_WINTER);
      },
      set(value) {
        this.setActiveFilter(FILTER_TYPE_DESTINATIONS_WINTER, value);
      },
    },

    durations: {
      get() {
        return this.getActiveFilter(FILTER_TYPE_DURATIONS);
      },
      set(value) {
        this.setActiveFilter(FILTER_TYPE_DURATIONS, value);
      },
    },
  },

  watch: {
    activeFilters: {
      deep: true,
      handler: 'updateFilters',
    },
  },

  async mounted() {
    this.initUrlFilters();
    await this.initActiveFilters();
    this.initOpenFilter();
  },

  methods: {
    close() {
      this.$emit('update:visible', false);
    },

    getActiveFilter(filter) {
      return this.activeFilters[filter];
    },

    async initActiveFilters() {
      this.updateActiveFiltersFromUrl();
      await this.$nextTick();

      if (!this.hasUrlFilters) {
        await this.initActiveSelectFilter(FILTER_TYPE_PACKAGE_TYPES);
        await this.initActiveDateRangeFilter(
          FILTER_TYPE_DEPARTURE_DATE_FROM,
          FILTER_TYPE_DEPARTURE_DATE_TO,
        );
        await this.initActiveSelectFilter(FILTER_TYPE_DURATIONS);
        await this.initActiveSelectFilter(FILTER_TYPE_AIRPORTS);
        await this.initActivePriceRangeFilter(FILTER_TYPE_PRICE_RANGE);
        await this.initActiveCheckboxesFilter(FILTER_TYPE_DESTINATIONS_SUMMER);
        await this.initActiveCheckboxesFilter(FILTER_TYPE_DESTINATIONS_WINTER);
        await this.initActiveCheckboxesFilter(FILTER_TYPE_EXCURSIONS);
        await this.initActiveCheckboxesFilter(FILTER_TYPE_ACCOMMODATION_TYPES);
        await this.initActiveCheckboxesFilter(FILTER_TYPE_HOST_TYPES);
        await this.initActiveCheckboxesFilter(FILTER_TYPE_TOUR_GROUPS);
      }

      this.$emit('setFiltersLoaded', true);
    },

    async initActiveCheckboxesFilter(modelName) {
      const selectedOptions = this.filters[modelName].options.filter(
        (option) => option.selected && option.enabled,
      );

      if (selectedOptions.length) {
        this[modelName] = selectedOptions.map((option) => option.id);
        await this.$nextTick();
      }
    },

    async initActiveDateRangeFilter(modelNameFrom, modelNameTo) {
      const dateFrom = this.filters.dates.default.from;
      const dateTo = this.filters.dates.default.to;

      if (dateFrom !== null) {
        this[modelNameFrom] = new Date(dateFrom);
        await this.$nextTick();
      }

      if (dateTo !== null) {
        this[modelNameTo] = new Date(dateTo);
        await this.$nextTick();
      }
    },

    async initActivePriceRangeFilter(modelName) {
      const { min, max } = this.filters[FILTER_TYPE_PRICE_RANGE];

      if (
        min.default !== null
        || max.default !== null
      ) {
        this[modelName] = [
          min.default !== null ? min.default : min.value,
          max.default !== null ? max.default : max.value,
        ];

        await this.$nextTick();
      }
    },

    async initActiveSelectFilter(modelName) {
      const selectedOptions = this.filters[modelName].options.filter(
        (option) => option.selected && option.enabled,
      );

      if (selectedOptions.length === 1 && this[modelName] === undefined) {
        this[modelName] = selectedOptions[0].id;
        await this.$nextTick();
      }
    },

    initOpenFilter() {
      const activeFilters = Object.keys(this.activeFilters);
      const openFilterType = (activeFilters.length ? activeFilters : this.checkboxFilters)[0];

      this.openFilter = this.filters[openFilterType].title;
    },

    initUrlFilters() {
      const url = new URL(window.location.href);
      const urlFilters = {};

      Array.from(url.searchParams.entries()).forEach((entry) => {
        const type = entry[0];
        const value = entry[1];

        switch (type) {
          case FILTER_TYPE_PACKAGE_TYPES: {
            urlFilters[type] = value;
            break;
          }

          case FILTER_TYPE_DEPARTURE_DATE_FROM:
          case FILTER_TYPE_DEPARTURE_DATE_TO: {
            const dateParts = value.split('-');
            urlFilters[type] = new Date(`${dateParts[0]}-${dateParts[1]}-${dateParts[2]}`);
            break;
          }

          case FILTER_TYPE_AIRPORTS:
          case FILTER_TYPE_DURATIONS: {
            urlFilters[type] = parseInt(value, 10);
            break;
          }

          case FILTER_TYPE_PRICE_RANGE:
          case FILTER_TYPE_DESTINATIONS_WINTER:
          case FILTER_TYPE_DESTINATIONS_SUMMER:
          case FILTER_TYPE_EXCURSIONS:
          case FILTER_TYPE_HOST_TYPES:
          case FILTER_TYPE_ACCOMMODATION_TYPES:
          case FILTER_TYPE_TOUR_GROUPS: {
            if (urlFilters[type] === undefined) {
              urlFilters[type] = [];
            }
            urlFilters[type].push(parseInt(value, 10));
            break;
          }

          // no default
        }
      });

      this.urlFilters = urlFilters;
    },

    setActiveFilter(filter, value) {
      const activeFilters = _.cloneDeep(this.activeFilters);

      if (value === undefined) {
        delete activeFilters[filter];
      } else {
        activeFilters[filter] = value;
      }

      this.$emit('update:activeFilters', activeFilters);
    },

    updateActiveFiltersFromUrl() {
      if (this.hasUrlFilters) {
        this.$emit('update:activeFilters', this.urlFilters);
      }
    },

    updateFilters(activeFilters) {
      this.loading = true;

      api.getFilterAvailablility(this.siteId, activeFilters)
        .then((response) => {
          this.$emit('update:filters', response.data);
        })
        .catch((error) => {
          console.error(error);
        })
        .then(() => {
          this.loading = false;
        });
    },

    updateOpenFilter(openFilter) {
      this.openFilter = this.openFilter === openFilter ? null : openFilter;
    },
  },
};
</script>

<style lang="scss" scoped>
  @import '../../assets/css/helpers/mixins/breakpoints';

  .packages-overview-filters {
    display: flex;
    flex-direction: column;
    flex-shrink: 0;
    background-color: white;

    @include tablet-and-smaller {
      position: fixed;
      top: 0;
      left: 0;
      z-index: 8008135;
      width: 100%;
      height: 100%;
      transition: transform .4s;
    }

    @include laptop-and-larger {
      width: var(--sidebar-width);
      margin-right: var(--sidebar-spacing);
      transform: translateY(-76px);
    }

    &:not(&--visible) {
      @include tablet-and-smaller {
        transform: translateY(100%);
      }
    }

    &__form {
      flex-grow: 1;

      @include tablet-and-smaller {
        overflow-x: hidden;
        overflow-y: auto;
      }

      &--loading {
        pointer-events: none;
      }
    }

    &__group {
      padding: var(--spacing-m);

      &:not(&--primary) {
        background-color: var(--color-grey-light);
        border: 1px solid var(--color-grey);
      }

      &--primary {
        background-color: var(--color-primary);
      }
    }

    &__input {
      margin-bottom: var(--spacing-m);
    }

    &__combiner {
      display: table;
      position: relative;
      z-index: 1;
      padding: 0 1ch;
      margin: calc(-1px - .5em - var(--spacing-m)) calc(50% - 40px) -.5em;
      background-color: white;
      color: rgba(0, 0, 0, .5);
      font-size: 14px;
      line-height: 1;
    }
  }
</style>
